import React, { ReactElement } from "react";
import { useNavigate } from "react-router-dom";
import { Loader, LoaderSizes, Button } from "@hid-galaxy-ui/galaxy-react";
import { useAppSelector } from "./redux";
import HIDCustomLogo from "./pages/auth/login/HIDCustomLogo";
import {
  useSessionAllModulesDenied,
  useSessionCanAccessAnyModule,
  useSessionCanAccessModule,
  useSessionCanEditAnyModule,
  useSessionCanEditModule,
  useSessionModuleDenied,
  useSessionPermissionsLoaded,
  useSessionPermissionsLoading,
} from "./hooks/useSessionPermissions";

/** Route guard using sessionPermissionMap. Without role_id, allows access (legacy). */
const SessionProtectedRoute: React.FC<{
  element: ReactElement;
  requiredPermissionKey: string;
  /** When set, OR-check these keys instead of only requiredPermissionKey. */
  requiredPermissionKeys?: string[];
  /** When true, view-only is not enough (e.g. create-new-partner). */
  requireEdit?: boolean;
}> = ({
  element,
  requiredPermissionKey,
  requiredPermissionKeys,
  requireEdit = false,
}) => {
  const navigate = useNavigate();
  const roleId = useAppSelector((state) => state.auth.loggedUserDetail?.role_id);
  const loading = useSessionPermissionsLoading();
  const loaded = useSessionPermissionsLoaded();
  const keys = requiredPermissionKeys ?? [requiredPermissionKey];
  const useAny = keys.length > 1;
  const canAccessSingle = useSessionCanAccessModule(keys[0] ?? "");
  const canEditSingle = useSessionCanEditModule(keys[0] ?? "");
  const deniedSingle = useSessionModuleDenied(keys[0] ?? "");
  const canAccessAny = useSessionCanAccessAnyModule(keys);
  const canEditAny = useSessionCanEditAnyModule(keys);
  const deniedAll = useSessionAllModulesDenied(keys);
  const canAccess = useAny ? canAccessAny : canAccessSingle;
  const canEdit = useAny ? canEditAny : canEditSingle;
  const denied = useAny ? deniedAll : deniedSingle;
  const allowed = requireEdit ? canEdit : canAccess;

  if (roleId && loading) {
    return (
      <div className="signing-in-container">
        <Loader variant={LoaderSizes.large} />
      </div>
    );
  }

  if (roleId && loaded && (denied || !allowed)) {
    return (
      <div className="signing-in-container">
        <div className="logo-wrapper">
          <HIDCustomLogo />
        </div>
        <div className="message-box">
          <p>Permission Denied</p>
          <Button variant="primary" label="Back" onClick={() => navigate(-1)} />
        </div>
      </div>
    );
  }

  return element;
};

export default SessionProtectedRoute;
